import { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { api, Match, LiveStatus } from '../api/client';
import { useAsync, Async } from '../components/Async';
import { MatchCard } from '../components/MatchCard';
import { LiveNotice } from '../components/LiveNotice';
import { formatDateLong, dayKey, dayNum, monthShort } from '../lib/format';
import { useMyPredictions } from '../lib/useMyPredictions';

const CalHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 24px 0 10px;

  .tile {
    flex: 0 0 auto;
    width: 56px;
    height: 56px;
    border-radius: 12px;
    background: ${({ theme }) => theme.colors.accent};
    color: #fff;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    line-height: 1;
  }
  .tile .d { font-size: 1.7rem; font-weight: 800; }
  .tile .m { font-size: 0.66rem; text-transform: uppercase; letter-spacing: 0.5px; margin-top: 2px; }
  .full { font-size: 1.1rem; font-weight: 700; }
`;

const RoundTag = styled.div`
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${({ theme }) => theme.colors.textMuted};
  margin: 10px 0 6px;
`;

// Etiqueta de ronda en español: en la llave usa el label del stage ("Dieciseisavos",
// "Octavos"...); en fase de grupos, el round ("Matchday N").
function roundLabel(m: Match): string {
  return m.stage?.label ?? m.round ?? 'Otros';
}

// Agrupa por día de calendario y, dentro de cada día, por ronda — preservando el orden.
function groupByDay(matches: Match[]): { day: string; iso: string | null; rounds: [string, Match[]][] }[] {
  const days: { day: string; iso: string | null; rounds: [string, Match[]][] }[] = [];
  for (const m of matches) {
    const dk = dayKey(m.kickoffUtc);
    let day = days[days.length - 1];
    if (!day || day.day !== dk) {
      day = { day: dk, iso: m.kickoffUtc, rounds: [] };
      days.push(day);
    }
    const round = roundLabel(m);
    const last = day.rounds[day.rounds.length - 1];
    if (last && last[0] === round) last[1].push(m);
    else day.rounds.push([round, [m]]);
  }
  return days;
}

function FixturesList({ matches, liveStatus }: { matches: Match[]; liveStatus?: LiveStatus }) {
  const preds = useMyPredictions();
  const days = groupByDay(matches);
  const hasLive = matches.some((m) => m.status === 'live');
  // Centrar en lo más reciente: último día con algún partido ya jugado.
  let target = 0;
  days.forEach((d, i) => {
    if (d.rounds.some(([, ms]) => ms.some((m) => m.status === 'finished'))) target = i;
  });
  const ref = useRef<HTMLElement | null>(null);
  useEffect(() => {
    ref.current?.scrollIntoView({ block: 'start' });
  }, []);

  return (
    <>
      <LiveNotice status={liveStatus} hasLive={hasLive} sticky />
      {days.map((day, i) => (
        <section key={day.day} ref={i === target ? ref : undefined} style={{ scrollMarginTop: '72px' }}>
          <CalHeader>
            <div className="tile">
              <span className="d">{dayNum(day.iso)}</span>
              <span className="m">{monthShort(day.iso)}</span>
            </div>
            <span className="full">{formatDateLong(day.iso)}</span>
          </CalHeader>
          {day.rounds.map(([round, ms]) => (
            <div key={round}>
              <RoundTag>{round}</RoundTag>
              {ms.map((m) => (
                <MatchCard key={m.id} m={m} pred={m.num != null ? preds.get(m.num) : undefined} />
              ))}
            </div>
          ))}
        </section>
      ))}
    </>
  );
}

export function Fixtures() {
  const state = useAsync(
    () => api.fixtures(),
    [],
    '/fixtures',
    (d) => d.matches.some((m) => m.status === 'live'),
  );
  return (
    <Async state={state}>
      {(d) => <FixturesList matches={d.matches} liveStatus={d.liveStatus} />}
    </Async>
  );
}
